import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useMemo } from 'react'

import type { RecentSpenderListProjection } from '@api'
import type { ColumnDef, PaginationState } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useUnit } from 'effector-react'

import { Skeleton } from '@/components/ui/skeleton'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'

import { useRecentSpendListMutations } from '../hooks/useRecentSpendListMutations.ts'
import { RecentSpenderActionButton } from './RecentSpenderActionButton.tsx'

interface RecentSpenderListTableProps {
  list: RecentSpenderListProjection[] | undefined
  isFetching: boolean
  paginationState: PaginationState
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>
  pageCount: number
}

export const RecentSpenderListTable = ({
  list,
  isFetching,
  paginationState,
  onPaginationChange,
  pageCount,
}: RecentSpenderListTableProps) => {
  const creatorId = useUnit($creatorId)
  const { deleteRecentSpendList, editRecentSpendList } =
    useRecentSpendListMutations(creatorId ?? 0)

  const handleDelete = useCallback(
    (list: RecentSpenderListProjection) => {
      openDialog({
        type: DIALOG_TYPE.DELETE_RECENT_SPENDER_LIST,
        listId: list.id!,
        onSubmit: () => {
          return deleteRecentSpendList.mutateAsync(list.id!)
        },
      })
    },
    [deleteRecentSpendList],
  )

  const handleEdit = useCallback(
    (list: RecentSpenderListProjection) => {
      openDialog({
        type: DIALOG_TYPE.RECENT_SPENDER_LIST_FORM,
        onSubmit: (values) => {
          return editRecentSpendList.mutateAsync({
            listId: list.id!,
            name: values.name,
            amountOfMinutes: values.minimumMinutes,
          })
        },
        title: 'Edit Recent Spend List',
        submitLabel: 'Save',
        initialValues: {
          name: list.name ?? '',
          minimumMinutes: list.amountOfMinutes ?? 0,
        },
      })
    },
    [editRecentSpendList],
  )

  const columns: ColumnDef<RecentSpenderListProjection>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'List Name',
        width: 200,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'amountOfMinutes',
        header: 'Time Spend',
        cell: ({ row }) => {
          return <span>{row.original.amountOfMinutes} minutes</span>
        },
        sortingFn: (a, b) => {
          return (
            (a.original.amountOfMinutes ?? 0) -
            (b.original.amountOfMinutes ?? 0)
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Date Created',
        sortingFn: (rowA, rowB) => {
          return (
            new Date(rowA.original.createdAt ?? '').getTime() -
            new Date(rowB.original.createdAt ?? '').getTime()
          )
        },
        cell: ({ row }) => {
          return (
            <span>
              {format(
                new Date(row.original.createdAt ?? ''),
                'LLL dd, y hh:mm a',
              )}
            </span>
          )
        },
        meta: {
          skeleton: {
            customCell: () => {
              return <Skeleton className="h-4 w-40 dark:bg-slate-500" />
            },
          },
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          return (
            <RecentSpenderActionButton
              onDelete={() => handleDelete(row.original)}
              onEdit={() => handleEdit(row.original)}
              row={row}
            />
          )
        },
        meta: {
          isActionColumn: true,
        },
      },
    ],
    [handleDelete, handleEdit],
  )

  return (
    <DataTable
      enablePagination
      columns={columns}
      data={list ?? []}
      enableSorting
      isLoading={isFetching}
      paginationState={paginationState}
      onPaginationChange={onPaginationChange}
      pageCount={pageCount}
      tbodyMinHeight={false}
      testId="recent-spender-list-table"
    />
  )
}
